// ============================================
// 敵の更新ロジック
// ============================================

import { CONFIG } from '../../config/game-config';
import {
    getEnemies,
    getPlayer,
    getLanes,
    getShieldActive, setShieldActive,
    setShieldTimer,
    setScreenShake
} from '../../store/index';
import { triggerPlayerDeath } from '../player/player';
import { getP5 } from '../../core/p5-instance';
import type { Enemy, LaserEnemy, SpeedyEnemy, MirrorEnemy } from '../../types/game-types';

/**
 * 敵の更新メイン
 */
export function updateEnemy(enemy: Enemy, enemySpeed: number, dt: number, index: number): void {
    if (enemy.type === 'laser') {
        updateLaserEnemy(enemy as LaserEnemy, enemySpeed, dt, index);
        return;
    }

    // 基本移動
    enemy.y += enemySpeed * dt;
    enemy.rotation += enemy.rotSpeed * dt;

    // タイプ別特殊移動
    if (enemy.type === 'speedy') {
        updateSpeedyEnemy(enemy as SpeedyEnemy, dt);
    }

    if (enemy.type === 'mirror') {
        updateMirrorEnemy(enemy as MirrorEnemy, dt);
    }
}

/**
 * SPEEDY敵の更新（ジグザグ移動）
 */
function updateSpeedyEnemy(enemy: SpeedyEnemy, dt: number): void {
    const p = getP5();

    enemy.zigzagTimer += dt;
    const interval = enemy.zigzagInterval;
    if (enemy.zigzagTimer > interval) {
        enemy.zigzagDir *= -1;
        enemy.zigzagTimer = 0;
    }
    enemy.x += enemy.zigzagDir * 3 * dt;
    enemy.x = p.constrain(enemy.x, 50, p.width - 50);
}

/**
 * MIRROR敵の更新（プレイヤーの反対レーンを追従）
 */
function updateMirrorEnemy(enemy: MirrorEnemy, dt: number): void {
    const p = getP5();
    const player = getPlayer();
    const lanes = getLanes();

    if (!player) {
        return;
    }

    const mirrorLane = (CONFIG.laneCount - 1) - player.lane;
    if (mirrorLane !== enemy.targetLane) {
        if (!enemy.isChangingLane) {
            enemy.isChangingLane = true;
            enemy.laneChangeTimer = 0;
        }
        enemy.laneChangeTimer += dt;
        if (enemy.laneChangeTimer >= enemy.laneChangeDelay) {
            enemy.targetLane = mirrorLane;
            enemy.isChangingLane = false;
            enemy.laneChangeTimer = 0;
        }
    }
    const targetX = lanes[enemy.targetLane];
    const mirrorLerpFactor = 1 - Math.pow(1 - 0.06, dt);
    enemy.x = p.lerp(enemy.x, targetX, mirrorLerpFactor);
    enemy.lane = enemy.targetLane;
}

/**
 * LASER敵の更新
 */
function updateLaserEnemy(enemy: LaserEnemy, enemySpeed: number, dt: number, index: number): void {
    const enemies = getEnemies();
    const player = getPlayer();
    if (!player) {
        return;
    }

    enemy.laserTimer += dt;

    if (enemy.laserState === 'moving') {
        enemy.y += enemySpeed * dt;
        if (enemy.y >= enemy.stopY) {
            enemy.y = enemy.stopY;
            enemy.laserState = 'charging';
            enemy.laserTimer = 0;
        }
    } else if (enemy.laserState === 'charging') {
        enemy.y = enemy.stopY;
        if (enemy.laserTimer >= enemy.chargeTime) {
            enemy.laserState = 'firing';
            enemy.laserTimer = 0;
            setScreenShake(5);
        }
    } else if (enemy.laserState === 'firing') {
        enemy.y = enemy.stopY;
        if (player.lane === enemy.lane && player.state?.name === 'Normal' && !(player.invincibleTimer && player.invincibleTimer > 0)) {
            if (getShieldActive()) {
                setShieldActive(false);
                setShieldTimer(0);
                setScreenShake(8);
            } else {
                triggerPlayerDeath();
            }
        }
        if (enemy.laserTimer >= enemy.laserDuration) {
            enemy.laserState = 'retreating';
            enemy.laserTimer = 0;
        }
    } else if (enemy.laserState === 'retreating') {
        enemy.y -= enemySpeed * 1.5 * dt;
        if (enemy.y < -50) {
            enemies.splice(index, 1);
        }
    }
}
